import type {} from '@mui/lab/themeAugmentation';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { alpha, createTheme as createMuiTheme } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import { ThemeOptions } from '../../theme/options';
import { LinkBehavior } from '../Link/LinkBehavior';

declare module '@mui/material/styles' {
  interface TypographyPropsVariantOverrides {
    large: true;
    medium: true;
    small: true;
  }

  interface TypeText {
    primary: string;
    secondary: string;
    disabled: string;
    info: string;
    success: string;
  }

  interface Palette {
    purple: Palette['primary'];
    tertiary: Palette['primary'];
    surface: {
      primary: string;
      secondary: string;
      tertiary: string;
      secondaryAlternative: string;
    };
  }

  interface PaletteOptions {
    tertiary: Palette['primary'];
    surface?: {
      primary: string;
      secondary: string;
      tertiary: string;
      secondaryAlternative: string;
    };
  }
}

declare module '@mui/material/Avatar' {
  type AvatarSize = 'small' | 'medium' | 'large' | number;
  interface AvatarOwnProps {
    size?: AvatarSize;
  }
}

declare module '@mui/material/TableRow' {
  interface TableRowOwnProps {
    variant?: 'click';
  }
}

declare module '@mui/material/TableCell' {
  interface TableCellPropsVariantOverrides {
    click: true;
    subhead: true;
  }
}

declare module '@mui/material/InputBase' {
  interface InputBasePropsColorOverrides {
    tertiary: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    tertiary: true;
    purple: true;
  }
}

declare module '@mui/material/SvgIcon' {
  interface SvgIconPropsColorOverrides {
    tertiary: true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonOwnProps {
    params?: Record<string, unknown>;
  }
}

export function createTheme(
  mode: PaletteMode,
  { createPalette, createComponents, ...options }: ThemeOptions,
) {
  const palette = createPalette(mode, createMuiTheme({}));
  const base = createMuiTheme({ spacing: 4, ...options, palette });

  return createMuiTheme(
    {
      components: {
        MuiTypography: {
          defaultProps: {
            variantMapping: {
              h1: 'h1',
              h2: 'h2',
              h3: 'h3',
              h4: 'h4',
              h5: 'h5',
              h6: 'h6',
              caption: 'span',
            },
          },
        },
        MuiAvatar: {
          defaultProps: {
            size: 'medium',
            variant: 'circular',
          },
          variants: [
            {
              props: { size: 'small' },
              style: {
                width: 32,
                height: 32,
                fontSize: 12,
                '& ~ .MuiBadge-badge': { width: 12, height: 12, minWidth: 12, padding: 0 },
              },
            },
            {
              props: { size: 'medium' },
              style: {
                width: 40,
                height: 40,
                fontSize: 16,
                '& ~ .MuiBadge-badge': { width: 16, height: 16, minWidth: 16, padding: 0 },
              },
            },
            {
              props: { size: 'large' },
              style: {
                width: 56,
                height: 56,
                fontSize: 18,
                '& ~ .MuiBadge-badge': { width: 18, height: 18, minWidth: 18, padding: 0 },
              },
            },
            {
              props: ({ size }) => typeof size === 'number',
              // TODO: fix this, but it works
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              style: ({ size }: any) => ({ width: size, height: size }),
            },
            {
              props: { color: 'purple' },
              style: {
                color: base.palette.purple.main,
                backgroundColor: alpha(base.palette.purple.main, 0.16),
              },
            },
            {
              props: { color: 'info' },
              style: {
                color: base.palette.info.main,
                backgroundColor: alpha(base.palette.info.main, 0.16),
              },
            },
            {
              props: { color: 'error' },
              style: {
                color: base.palette.error.main,
                backgroundColor: alpha(base.palette.error.main, 0.16),
              },
            },
          ],
          styleOverrides: {
            root: {
              color: base.palette.text.secondary,
              backgroundColor: base.palette.surface.secondary,
            },
          },
        },
        MuiLink: {
          defaultProps: {
            component: LinkBehavior,
          },
        },
        MuiButtonBase: {
          defaultProps: {
            disableRipple: true,
            LinkComponent: LinkBehavior,
          },
          styleOverrides: {
            root: {
              '&:focus-visible:not(:active)': {
                outlineWidth: base.spacing(0.5),
                outlineOffset: base.spacing(0.5),
                outlineStyle: 'solid',
                outlineColor: base.palette.secondary.dark,
              },
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            sizeSmall: {
              '& .MuiSvgIcon-root': {
                width: 16,
                height: 16,
              },
            },
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: {
              backgroundColor: 'transparent',
              '& .MuiButtonBase-root': {
                backgroundColor: 'transparent',
                '&:focus-visible:not(:active)': {
                  outlineOffset: base.spacing(-0.5),
                  borderRadius: Number(base.shape.borderRadius ?? 0) * 2,
                },
              },
            },
          },
        },
        MuiAutocomplete: {
          styleOverrides: {
            root: {
              '& .Mui-disabled': {
                '& .MuiAutocomplete-endAdornment': { display: 'none' },
              },
            },
          },
        },
        MuiListItem: {
          styleOverrides: {
            root: {
              gap: base.spacing(4),
              alignItems: 'center',
            },
          },
        },
        MuiListItemAvatar: {
          styleOverrides: {
            root: {
              width: 40,
              margin: 0,
              minWidth: 40,
            },
          },
        },
        MuiListItemButton: {
          defaultProps: {
            LinkComponent: LinkBehavior,
          },
          styleOverrides: {
            root: {
              gap: '16px',
              alignItems: 'center',
              '&:hover': {
                background: base.palette.secondary.light,
              },
              '&.Mui-selected': {
                background: base.palette.secondary.main,
                '&:hover': {
                  background: base.palette.secondary.main,
                },
              },
              '&:focus-visible:not(:active)': {
                outline: 'none',
              },
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              gap: '16px',
              alignItems: 'center',
              '&:hover': {
                background: base.palette.secondary.light,
              },
              '&.Mui-selected': {
                background: base.palette.secondary.main,
                '&:hover': {
                  background: base.palette.secondary.main,
                },
              },
              '&:focus-visible:not(:active)': {
                outline: 'none',
              },
            },
          },
        },
        MuiListItemText: {
          styleOverrides: {
            root: {
              margin: 0,
              maxWidth: 'calc(100% - 80px)', // needed for long addresses
            },
          },
        },
        MuiListItemIcon: {
          styleOverrides: {
            root: {
              minWidth: 'unset',
            },
          },
        },
        MuiGrid: {
          defaultProps: {
            display: 'flex',
          },
        },
        MuiSvgIcon: {
          styleOverrides: {
            root: {
              width: 20,
              height: 20,
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              margin: 0,
              borderRadius: Number(base.shape.borderRadius ?? 0) * 2,
              fontSize: 12,
              fontWeight: 500,
              height: 'unset',
              padding: base.spacing(1, 2),
            },
            colorPrimary: {
              color: base.palette.tertiary.contrastText,
            },
            icon: {
              color: base.palette.tertiary.contrastText,
              '&:hover': {
                color: base.palette.tertiary.contrastText,
              },
            },
            sizeSmall: {
              height: 28,
            },
          },
        },
        MuiDrawer: {
          styleOverrides: {
            paper: {
              background: base.palette.background.default,
              borderRadius: 0,
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            root: {
              width: '100%',
              minHeight: 'unset',
              backgroundColor: base.palette.secondary.light,
              borderRadius: Number(base.shape.borderRadius ?? 0) * 2,
              padding: base.spacing(0.5),
            },
            indicator: {
              display: 'none',
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              flex: 1,
              padding: base.spacing(2),
              minHeight: 'unset',
              borderRadius: Number(base.shape.borderRadius ?? 0) * 2,
              '&.Mui-selected': {
                backgroundColor: base.palette.surface.primary,
              },
            },
          },
        },
        MuiFormLabel: {
          defaultProps: {
            required: false,
          },
          styleOverrides: {
            root: {
              color: base.palette.text.secondary,
              '&.Mui-disabled': {
                color: base.palette.text.secondary,
              },
              '&.Mui-focused': {
                color: base.palette.text.secondary,
              },
              '&.Mui-error': {
                color: base.palette.error.main,
              },
            },
          },
        },
        MuiInputLabel: {
          defaultProps: {
            required: false,
          },
          styleOverrides: {
            root: {
              color: base.palette.text.secondary,
              '&.Mui-disabled': {
                color: base.palette.text.secondary,
              },
              '&.Mui-focused': {
                color: base.palette.text.secondary,
              },
              '&.Mui-error': {
                color: base.palette.error.main,
              },
            },
          },
        },
        MuiStepButton: {
          styleOverrides: {
            root: {
              '&:focus-visible:not(:active)': {
                outline: 'none',

                //TODO: add focus style
              },
            },
          },
        },
        MuiTableRow: {
          variants: [
            {
              props: { variant: 'click' },
              style: {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: base.palette.surface.secondary,
                },
              },
            },
          ],
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              border: 'none',
              padding: base.spacing(3),
              '&:first-of-type': {
                padding: base.spacing(3, 3, 3, 4),
              },
              '&:last-of-type': {
                padding: base.spacing(3, 4, 3, 3),
              },
            },
            head: {
              background: 'none',
              color: base.palette.text.secondary,
              fontWeight: 500,
              borderBottom: `1px solid ${base.palette.surface.secondary}`,
            },
          },
          variants: [
            {
              props: { variant: 'subhead' },
              style: {
                background: 'none',
                color: base.palette.text.secondary,
                fontWeight: 500,
                borderBottom: 'none',
              },
            },
          ],
        },
        MuiLinearProgress: {
          styleOverrides: {
            root: {
              width: '100%',
              height: base.spacing(2),
              display: 'block',
              borderRadius: base.shape.borderRadius,
            },
            colorPrimary: {
              backgroundColor: alpha(base.palette.info.light, 0.16),
            },
            barColorPrimary: {
              backgroundColor: base.palette.info.main,
            },
          },
        },
      },
    },
    base,
    { components: createComponents(base) },
  );
}
